// Datakilden — tilkoblingen til KOP-broen, og ærligheten om at den ikke finnes.
//
// STATUS I DAG: det finnes INGEN KOP-bro. Denne fila kobler til adressen den får
// (`VITE_KOP_WS`), og hvis adressen ikke er satt, går den ikke i «laster» for
// alltid — den sier `ikke-konfigurert` og forteller hva som mangler. Det er
// forskjellen mellom en app som er uferdig og en app som later som (§10).
//
// TILKOBLINGSTILSTANDEN ER FIRE, IKKE TO. «Tilkoblet/ikke tilkoblet» skjuler de
// to tilstandene som betyr noe i felt: at vi ALDRI har vært tilkoblet (feil
// adresse, bro ikke satt opp) og at vi MISTET forbindelsen (samband falt) — den
// siste betyr at det som står på skjermen er historie, og det må operatøren se.
//
// VALIDERING: meldinger fra broen valideres strukturelt før de slippes inn i
// lageret. Ideelt skjer det med et zod-skjema i `@skade/contracts` (som
// `RadarMelding` gjør), men kontrakten finnes ikke ennå, så vaktene her er
// håndskrevne og bevisst strenge: en melding vi ikke forstår, LOGGES og
// forkastes — den skal ikke bli en halvt utfylt kontakt på kartet.
import type { Ovelsestilkobling } from '@skade/sensorflate';
import type { KopKommando, KopMelding, Kystkontakt, KopSensor, Observasjon } from './typer.js';

/**
 * `ikke-konfigurert` bærer TRE tekster og ikke én, og rekkefølgen på dem er
 * hierarkiet fra `manglerKilde` i `@skade/sensorflate`:
 *
 *   `forklaring` — norsk, hel setning, det operatøren leser FØRST.
 *   `neste`      — hva han gjør nå. Uten dette er ærligheten hjelpeløs.
 *   `variabel`   — miljøvariabelen, for DRIFT. Dempet, men aldri fjernet.
 *
 * Før sto variabelnavnet i `adresse`, og da var det en miljøvariabel som
 * dominerte skjermbildet: `Forsøkt: (VITE_KOP_WS er ikke satt)`. En operatør kan
 * ikke sette en byggvariabel, og han kan ikke lese noe operativt ut av navnet.
 * `adresse` er derfor tom i denne tilstanden — det finnes ingen adresse vi har
 * forsøkt — og navnet står der navn hører, som driftsdetalj.
 */
export type Tilkobling =
  /** Ingen adresse satt. Ikke en feil — broen er ikke rullet ut. */
  | { tilstand: 'ikke-konfigurert'; adresse: ''; forklaring: string; neste: string; variabel: string }
  | { tilstand: 'kobler'; adresse: string }
  | { tilstand: 'tilkoblet'; adresse: string; sidenMs: number }
  /** Vi har mistet (eller aldri fått) forbindelsen. `arsak` vises ordrett. */
  | { tilstand: 'nede'; adresse: string; arsak: string; forsok: number }
  /**
   * ØVINGSDATA. Egen tilstand fra `@skade/sensorflate`, og den later ALDRI som
   * den er tilkoblet — se `OVELSE_TILKOBLING`. Før var dette en lokal `demo`-
   * variant som `main.ts` måtte caste seg inn i (`as unknown as Tilkobling`);
   * casten er borte, og øvingstilstanden er nå en ekte gren i unionen som hver
   * `switch` må ta stilling til.
   */
  | Ovelsestilkobling;

export interface Kilde {
  tilkobling(): Tilkobling;
  /** Abonner på meldinger. Returnerer avmelding. */
  påMelding(h: (m: KopMelding) => void): () => void;
  /** Abonner på tilkoblingsendringer. Returnerer avmelding. */
  påTilkobling(h: (t: Tilkobling) => void): () => void;
  /** Send en kommando. `false` = kunne ikke sendes NÅ, og kalleren må si det. */
  send(k: KopKommando): boolean;
  lukk(): void;
}

/**
 * Adressen til KOP-broen. Tom verdi er en GYLDIG og forventet tilstand i dag,
 * ikke en feil å skjule — mates inn ved bygg, som `VITE_DUGNAD_API` i
 * prosjektmodulen og `VITE_BUILD_ID` i COP.
 */
export const KOP_WS = (import.meta.env['VITE_KOP_WS'] ?? '').trim();

/**
 * Tekstene for «ingen bro». De ligger som konstanter her — der adressen bor —
 * fordi de er en del av kildens tilstand, ikke av et skjermbilde: to paneler som
 * beskriver samme mangel med to ulike setninger er to sannheter.
 */
const IKKE_KONFIGURERT: Omit<Extract<Tilkobling, { tilstand: 'ikke-konfigurert' }>, 'tilstand'> = {
  adresse: '',
  forklaring: 'Ingen sensorbro er satt opp for denne posten ennå',
  neste:
    'Dette er ikke en feil du kan rette fra skjermen — broen må rulles ut på posten. Meld variabelnavnet '
    + 'under til drift. I mellomtiden kan du slå på ØVINGSDATA og trene på flata; den er tydelig merket og '
    + 'kan ikke sende noe til SKADE.',
  variabel: 'For drift: byggvariabelen VITE_KOP_WS er ikke satt (adressen til KOP-broens WebSocket)',
};

/**
 * Koble til broen. Reconnect med voksende pause (1 s → 30 s): en post med dårlig
 * samband skal ikke hamre på nettet, men den skal heller ikke gi opp — det er
 * grunnloven §6, «anta dårlig samband».
 */
export function lagKilde(adresse = KOP_WS): Kilde {
  const meldingsLyttere = new Set<(m: KopMelding) => void>();
  const tilkoblingsLyttere = new Set<(t: Tilkobling) => void>();
  let ws: WebSocket | null = null;
  let lukket = false;
  let forsok = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let tilstand: Tilkobling = adresse
    ? { tilstand: 'kobler', adresse }
    : { tilstand: 'ikke-konfigurert', ...IKKE_KONFIGURERT };

  function sett(t: Tilkobling): void {
    tilstand = t;
    for (const h of tilkoblingsLyttere) h(t);
  }

  function koble(): void {
    if (lukket || !adresse) return;
    sett({ tilstand: 'kobler', adresse });
    let sokkel: WebSocket;
    try {
      sokkel = new WebSocket(adresse);
    } catch (e) {
      // En ugyldig URL kaster synkront. Da er det ikke sambandet som er nede —
      // det er konfigurasjonen, og operatøren skal få lese feilen ordrett.
      sett({ tilstand: 'nede', adresse, arsak: `Ugyldig adresse: ${feiltekst(e)}`, forsok: ++forsok });
      return;
    }
    ws = sokkel;
    sokkel.addEventListener('open', () => {
      forsok = 0;
      sett({ tilstand: 'tilkoblet', adresse, sidenMs: Date.now() });
    });
    sokkel.addEventListener('message', (ev) => {
      const m = tolkMelding(ev.data);
      if (!m) return;
      for (const h of meldingsLyttere) h(m);
    });
    sokkel.addEventListener('error', () => {
      // `error` gir aldri en grunn i nettleseren. Å finne på en ville vært verre
      // enn å innrømme at vi ikke vet.
      sett({ tilstand: 'nede', adresse, arsak: 'Nettleseren rapporterte en tilkoblingsfeil uten årsak', forsok: forsok + 1 });
    });
    sokkel.addEventListener('close', (ev) => {
      if (lukket) return;
      forsok++;
      sett({
        tilstand: 'nede',
        adresse,
        arsak: ev.reason || `Forbindelsen lukket (kode ${ev.code})`,
        forsok,
      });
      const pause = Math.min(30_000, 1000 * 2 ** Math.min(forsok, 5));
      timer = globalThis.setTimeout(koble, pause);
    });
  }

  koble();

  return {
    tilkobling: () => tilstand,
    påMelding(h) { meldingsLyttere.add(h); return () => meldingsLyttere.delete(h); },
    påTilkobling(h) { tilkoblingsLyttere.add(h); return () => tilkoblingsLyttere.delete(h); },
    send(k) {
      if (!ws || ws.readyState !== WebSocket.OPEN) return false;
      try { ws.send(JSON.stringify(k)); return true; } catch { return false; }
    },
    lukk() {
      lukket = true;
      if (timer !== null) globalThis.clearTimeout(timer);
      ws?.close();
    },
  };
}

function feiltekst(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

// ── Vakter (midlertidig erstatning for et zod-skjema i contracts) ────────────

function erObjekt(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}
function tall(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}
function tekst(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}

/**
 * JSON → `KopMelding`, eller `null`.
 *
 * Vakta er streng på de feltene UI-et REGNER på (tid, peiling, id) og slapp på
 * resten. Det er et bevisst valg: en manglende `merknad` er ufarlig, mens et
 * `tsMs` som er en streng gir en kontakt med alder «NaN s» — og en NaN i en
 * alder er nettopp en «stille feil», det verste denne appen kan produsere.
 */
export function tolkMelding(rå: unknown): KopMelding | null {
  let o: unknown = rå;
  if (typeof rå === 'string') {
    try { o = JSON.parse(rå); } catch { return null; }
  }
  if (!erObjekt(o) || !tekst(o['type'])) return null;
  switch (o['type']) {
    case 'post':
      return erObjekt(o['post']) && tekst(o['post']['id']) ? (o as unknown as KopMelding) : null;
    case 'sensor':
      return erSensor(o['sensor']) ? (o as unknown as KopMelding) : null;
    case 'observasjon':
      return erObservasjon(o['observasjon']) ? (o as unknown as KopMelding) : null;
    case 'kontakt':
      return erKontakt(o['kontakt']) ? (o as unknown as KopMelding) : null;
    case 'kontakt-borte':
      return tekst(o['kontaktId']) ? ({ type: 'kontakt-borte', kontaktId: o['kontaktId'], arsak: typeof o['arsak'] === 'string' ? o['arsak'] : 'Broen oppgav ingen årsak' }) : null;
    case 'sensor-borte':
      return tekst(o['sensorId']) ? ({ type: 'sensor-borte', sensorId: o['sensorId'], arsak: typeof o['arsak'] === 'string' ? o['arsak'] : 'Broen oppgav ingen årsak' }) : null;
    default:
      return null;
  }
}

const ARTER = new Set(['radar', 'rf', 'hydrofon', 'kamera']);

export function erSensor(v: unknown): v is KopSensor {
  return erObjekt(v) && tekst(v['id']) && tekst(v['navn']) && ARTER.has(String(v['art']));
}

const SLAG = new Set(['peiling', 'peiling-avstand', 'frekvens', 'lydsignatur', 'bilde']);

export function erObservasjon(v: unknown): v is Observasjon {
  if (!erObjekt(v) || !tekst(v['id']) || !tekst(v['sensorId']) || !tall(v['tsMs'])) return false;
  const m = v['maaling'];
  if (!erObjekt(m) || !SLAG.has(String(m['slag']))) return false;
  // Målingen må bære det den lover å bære. En «peiling-avstand» uten avstand er
  // ikke en radarmåling med et manglende felt — det er en peiling som utgir seg
  // for å være mer, og den ville blitt en målløsning på gjettet avstand.
  if (m['slag'] === 'peiling' && !tall(m['peilingDeg'])) return false;
  if (m['slag'] === 'peiling-avstand' && !(tall(m['peilingDeg']) && tall(m['avstandM']))) return false;
  if (m['slag'] === 'frekvens' && !tall(m['frekvensHz'])) return false;
  if (m['slag'] === 'lydsignatur' && !tekst(m['signaturNavn'])) return false;
  // En bilde-observasjon uten både bilde OG peiling bærer ingen informasjon —
  // den ville blitt en tom rad i kontaktkortet som ser ut som en observasjon.
  if (m['slag'] === 'bilde' && !tekst(m['bildeUrl']) && !tall(m['peilingDeg'])) return false;
  return true;
}

export function erKontakt(v: unknown): v is Kystkontakt {
  if (!erObjekt(v) || !tekst(v['id']) || !tall(v['sisteTsMs'])) return false;
  const obs = v['observasjoner'];
  return Array.isArray(obs) && obs.every(erObservasjon);
}
